// ─────────────────────────────────────────────────
// Pricing — Marketing Page Data
// Client-safe pricing data (no process.env, pure ES module)
// Source of truth: shared/plans.js — keep in sync
// ─────────────────────────────────────────────────

export const pricingData = {
  tiers: [
    {
      id: 'free',
      name: 'Free',
      marketingName: 'Free',
      idealFor: 'Exploring the platform',
      prices: { monthly: 0, yearly: 0 },
      cta: { label: 'Get Started Free', href: '/onboarding/identity' },
      featured: false,
      borderStyle: 'default',
      featureHighlights: [
        '10 tracked keywords',
        '1 domain',
        '2 competitors per domain',
        'Weekly SERP checks',
        'Email alerts',
        '7-day data retention',
      ],
    },
    {
      id: 'essential',
      name: 'Essential',
      marketingName: 'Essential',
      idealFor: 'Solo Founders & SMBs',
      prices: { monthly: 79, yearly: 63 },
      cta: { label: 'Get Started', href: '/onboarding/identity' },
      featured: false,
      borderStyle: 'default',
      featureHighlights: [
        '250 tracked keywords',
        '5 domains with 5 competitors each',
        'Daily SERP checks',
        'Visual monitoring every 3 days',
        '10 screenshot monitors',
        'Tag filtering in Pulse feed',
        'Team management (3 seats)',
        'Pulse comments & @mentions',
        'Email alerts',
        '90-day data retention',
      ],
    },
    {
      id: 'pro',
      name: 'Pro',
      marketingName: 'Professional',
      idealFor: 'High-Growth Marketing Teams',
      prices: { monthly: 199, yearly: 159 },
      cta: { label: 'Unlock Full Insights', href: '/onboarding/identity' },
      featured: true,
      borderStyle: 'cyan',
      featureHighlights: [
        '1,000 tracked keywords',
        '30 domains with 10 competitors each',
        'Daily SERP & visual checks',
        '20 screenshot monitors with Smart-Mute',
        'Desktop & mobile screenshots',
        'Slack, Discord & webhook alerts',
        'GA4 & Search Console overlays',
        'Team management (10 seats)',
        'Client portal for stakeholders',
        'Authority Bridge link opportunities',
        'AI-powered report insights',
        'Tag correlation engine with visibility overlay',
        '180-day data retention',
      ],
    },
    {
      id: 'team',
      name: 'Team',
      marketingName: 'Enterprise',
      idealFor: 'Agencies & Industry Leaders',
      prices: { monthly: 299, yearly: 239 },
      cta: { label: 'Connect with our Team', href: '/onboarding/identity' },
      featured: false,
      borderStyle: 'violet',
      featureHighlights: [
        '2,500 tracked keywords',
        '999 domains with 20 competitors each',
        '6-hour visual monitoring',
        '50 screenshot monitors',
        'Desktop & mobile screenshots',
        'All alert channels including SMS',
        'Team management (50 seats)',
        'Client portal & Search Console',
        'Authority Bridge with deep SERP crawl',
        'Digital shelf dashboard',
        '365-day data retention',
      ],
    },
  ],

  comparisonTable: [
    {
      category: 'Keyword & SERP Intelligence',
      rows: [
        { label: 'Tracked keywords', values: ['10', '250', '1,000', '2,500'] },
        { label: 'Search engines', values: ['1', '3', '3', '3'] },
        { label: 'SERP check frequency', values: ['Weekly', 'Daily', 'Daily', 'Daily'] },
        { label: 'Domains', values: ['1', '5', '30', '999'] },
        { label: 'Competitors per domain', values: ['2', '5', '10', '20'] },
        { label: 'Share of Voice tracking', values: [false, false, true, true] },
        { label: 'Win/Loss analysis', values: [false, false, true, true] },
      ],
    },
    {
      category: 'Discovery & Monitoring',
      rows: [
        { label: 'Discovery URLs', values: ['2', '10', '20', '40'] },
        { label: 'RSS feed monitoring', values: ['Daily', 'Daily', 'Hourly', 'Hourly'] },
        { label: 'Sitemap change detection', values: ['Weekly', 'Daily', 'Daily', 'Daily'] },
        { label: 'Social media stats', values: [false, false, true, true] },
        { label: 'Authority Bridge scans/mo', values: [false, false, '2', '4'] },
        { label: 'Authority Bridge SERP depth', values: [false, false, '20', '100'] },
      ],
    },
    {
      category: 'Visual Intelligence',
      rows: [
        { label: 'Screenshot monitors', values: ['1', '10', '20', '50'] },
        { label: 'Screenshots per month', values: ['30', '300', '600', '1,500'] },
        { label: 'Screenshot frequency', values: ['Every 3 days', 'Every 3 days', 'Daily', 'Every 6 hours'] },
        { label: 'Visual diff', values: ['Basic', 'Basic', 'Full', 'Full'] },
        { label: 'DOM diff tracking', values: [false, 'Basic', 'Full', 'Full'] },
        { label: 'Smart-Mute noise filter', values: [false, false, true, true] },
        { label: 'Exclusion regions', values: [false, false, true, true] },
        { label: 'Multi-device capture', values: [false, false, true, true] },
      ],
    },
    {
      category: 'Technical Intelligence',
      rows: [
        { label: 'Tech audits', values: ['3 lifetime', '5/month', '30/month', '60/month'] },
        { label: 'Performance benchmarking', values: [false, false, true, true] },
        { label: 'Tech stack detection', values: [false, false, true, true] },
        { label: 'A/B test detection', values: [false, false, true, true] },
      ],
    },
    {
      category: 'Reporting & Analytics',
      rows: [
        { label: 'Executive reports', values: [false, 'Basic', 'Full', 'Full'] },
        { label: 'AI-powered insights', values: [false, false, true, true] },
        { label: 'Weekly competitive intelligence briefing', values: [false, false, true, true] },
        { label: 'Tag correlation engine', values: [false, false, true, true] },
        { label: 'Competitor activity chart', values: [false, false, true, true] },
        { label: 'Visibility overlay', values: [false, false, true, true] },
        { label: 'AI correlation summary', values: [false, false, true, true] },
        { label: 'Scenario presets', values: [false, false, true, true] },
        { label: 'Campaign tags', values: [false, false, '10', 'Unlimited'] },
      ],
    },
    {
      category: 'Integrations & Alerts',
      rows: [
        { label: 'Email alerts', values: [true, true, true, true] },
        { label: 'Slack', values: [false, false, true, true] },
        { label: 'Discord', values: [false, false, true, true] },
        { label: 'Microsoft Teams', values: [false, false, true, true] },
        { label: 'Webhooks', values: [false, false, true, true] },
        { label: 'SMS', values: [false, false, false, true] },
        { label: 'Google Analytics overlay', values: [false, false, true, true] },
        { label: 'Google Search Console overlay', values: [false, false, true, true] },
      ],
    },
    {
      category: 'Team & Collaboration',
      rows: [
        { label: 'Team management', values: [false, true, true, true] },
        { label: 'Team member seats', values: ['0', '3', '10', '50'] },
        { label: 'Pulse comments & @mentions', values: [false, true, true, true] },
        { label: 'Chart notes & annotations', values: [false, true, true, true] },
        { label: 'Client portal', values: [false, false, true, true] },
      ],
    },
    {
      category: 'Data & Storage',
      rows: [
        { label: 'Data retention', values: ['7 days', '90 days', '180 days', '365 days'] },
        { label: 'Screenshot retention', values: ['30 days', '90 days', '180 days', '365 days'] },
      ],
    },
  ],

  faqs: [
    {
      question: 'Can I switch plans at any time?',
      answer: 'Yes. Upgrades are prorated and take effect immediately. Downgrades apply at the end of your current billing cycle.',
    },
    {
      question: 'What happens when I hit a limit?',
      answer: "You'll see a clear usage indicator in your dashboard. We'll notify you before you reach your cap so you can upgrade or adjust your monitoring scope.",
    },
    {
      question: 'How does the free plan work?',
      answer: "The free plan includes 10 keywords, 1 domain, and weekly SERP checks \u2014 no credit card required. It's a great way to explore the platform before committing to a paid plan.",
    },
    {
      question: 'Do you offer custom plans?',
      answer: "Yes. If you need higher limits, custom integrations, or dedicated support, reach out to our team and we'll build a plan that fits your organization.",
    },
    {
      question: 'How does Tivion handle data collection?',
      answer: 'We manage all infrastructure, data harvesting, and analysis in-house. Your competitive intelligence is end-to-end encrypted and only accessible to your team.',
    },
    {
      question: "What's included in AI-powered insights?",
      answer: 'Our AI engine generates executive summaries of your competitive landscape and tag correlation summaries that explain how competitor site changes correlate with their ranking gains \u2014 highlighting key movements, correlations, and recommended actions. Available on Professional and Enterprise plans.',
    },
  ],
};
